<template>
  <HeaderDashboardCards
    data-testid="positions-header-dashboard"
    aria-label="Positions summary"
    :metrics="cards"
    variant="count-only"
    :action-label="actionLabel"
    @select="handleSelect"
  />
</template>

<script setup>
import { computed } from "vue";
import HeaderDashboardCards from "@components/HeaderDashboardCards/HeaderDashboardCards.vue";

const props = defineProps({
  activeCount: {
    type: Number,
    default: 0,
  },
  draftCount: {
    type: Number,
    default: 0,
  },
  invalidCount: {
    type: Number,
    default: 0,
  },
  vacantCount: {
    type: Number,
    default: 0,
  },
  /**
   * Key of the card whose filter is currently applied to the list
   * ('active' | 'draft' | 'invalid' | 'vacant'). Used for the pressed state
   * only; clicking an active card again resets to the default filter state.
   */
  activeIndicator: {
    type: String,
    default: null,
    validator: (v) => v === null || ["active", "draft", "invalid", "vacant"].includes(v),
  },
  /**
   * Human action verb used only in each card's accessible name
   * (e.g. "Apply Filter" / "Clear Filter"). Not rendered as visible text so the
   * cards stay current-count-only.
   */
  actionLabel: {
    type: String,
    default: "Apply Filter",
  },
  /** When false (product Readiness off), hide the Invalid summary card. */
  showInvalidCard: {
    type: Boolean,
    default: true,
  },
  /** ER-12597: the list request failed, so no real totals exist; every card shows "—" instead of a count. */
  loadFailed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["select-indicator"]);

const FAILED_COUNT = "—";

const countOrDash = (count) => (props.loadFailed ? FAILED_COUNT : count);

const cards = computed(() => {
  const all = [
    {
      key: "active",
      title: "Active",
      count: countOrDash(props.activeCount),
      isRed: !props.loadFailed && props.activeCount === 0,
      clickable: true,
      active: props.activeIndicator === "active",
    },
    {
      key: "draft",
      title: "Draft",
      count: countOrDash(props.draftCount),
      isRed: false,
      clickable: true,
      active: props.activeIndicator === "draft",
    },
    {
      key: "invalid",
      title: "Invalid",
      count: countOrDash(props.invalidCount),
      isRed: !props.loadFailed && props.invalidCount > 0,
      clickable: true,
      active: props.activeIndicator === "invalid",
    },
    {
      key: "vacant",
      title: "Vacant",
      count: countOrDash(props.vacantCount),
      clickable: true,
      isRed: !props.loadFailed && props.vacantCount > 0,
      active: props.activeIndicator === "vacant",
    },
  ];
  return props.showInvalidCard ? all : all.filter((card) => card.key !== "invalid");
});

function handleSelect(key) {
  emit("select-indicator", key);
}
</script>
